import { User } from "@/lib/types/auth";

// Storage keys
const AUTH_USER_KEY = "auth_user";
const ACCESS_TOKEN_KEY = "auth_access_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";

// User storage functions
export const setUserInStorage = (user: User): void => {
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const getUserFromStorage = (): User | null => {
  const userString = localStorage.getItem(AUTH_USER_KEY);
  if (!userString) return null;
  return JSON.parse(userString) as User;
};

export const clearUserFromStorage = (): void => {
  localStorage.removeItem(AUTH_USER_KEY);
};

// Token storage functions
export const setAccessToken = (token: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const clearAccessToken = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
};

export const setRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Combined auth data functions
export const setAuthData = (
  user: User,
  access: string,
  refresh: string
): void => {
  // Save the full user object exactly as it comes from backend
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));

  setAccessToken(access);
  setRefreshToken(refresh);
};

// Update only user data (for permission refreshes)
export const updateUserData = (user: User): void => {
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};


export const getAuthData = (): {
  user: User | null;
  acesss: string | null;
  refresh: string | null;
} => {
  return {
    user: getUserFromStorage(),
    acesss: getAccessToken(),
    refresh: getRefreshToken(),
  };
};

export const checkAuthStatus = (): User | null => {
  const user = getUserFromStorage();
  const acesss = getAccessToken();
  const refresh = getRefreshToken();

  // If we have all pieces, assume valid (interceptor will handle refresh if needed)
  if (user && acesss && refresh) {
    return user;
  }

  // Missing pieces = not authenticated
  clearAllAuthData();
  return null;
};

export const clearAllAuthData = (): void => {
  clearUserFromStorage();
  clearAccessToken();
  clearRefreshToken();
};

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const acesss = getAccessToken();
  const user = getUserFromStorage();
  return !!(acesss && user);
};

// Helper function to get Authorization header value
export const getAuthHeader = (): string | null => {
  const acesss = getAccessToken();
  return acesss ? `Bearer ${acesss}` : null;
};
